import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    TextField,
    IconButton,
    CircularProgress,
    FormControl,
    Select,
    MenuItem,
    useTheme,
    useMediaQuery,
    Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import eventsApi from '../../../api/eventsApi/eventsApi';
import { notifySpeechTranscriptionError } from '../../../services/speechTranscriptionNotify';
import { useSelector } from 'react-redux';


interface CategoryOption {
    id: string;
    name: string;
    index: number | null;
}

interface ExistingFile {
    id: string;
    fileName: string;
    /** 1 bulgu, 2 öneri; yok/null = eski kayıt (bulgu) */
    attachmentSection?: number | null;
}

const BulguEklePage = () => {
    const { id, questionId } = useParams<{ id: string; questionId?: string }>();
    const navigate = useNavigate();
    const theme = useTheme();
    const isCompact = useMediaQuery(theme.breakpoints.down('md'));
    const sidebarWidth = isCompact ? 64 : 250;
    const userId = useSelector((state: any) => state.auth.id);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [pinName, setPinName] = useState('');
    const [findings, setFindings] = useState('');
    const [proposedSolutions, setProposedSolutions] = useState('');

    const [questionText, setQuestionText] = useState('');
    const [linkedCategoryName, setLinkedCategoryName] = useState('');
    const [isGeneratingSuggestion, setIsGeneratingSuggestion] = useState(false);

    const isStandalone = !questionId;
    const [categories, setCategories] = useState<CategoryOption[]>([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState('');
    const [selectedQuestionId, setSelectedQuestionId] = useState('');
    const [categoryQuestions, setCategoryQuestions] = useState<{ id: string; text: string }[]>([]);

    // File attachments (bulgu / öneri ayrı)
    const [newFindingsFiles, setNewFindingsFiles] = useState<File[]>([]);
    const [newProposedFiles, setNewProposedFiles] = useState<File[]>([]);
    const [existingFiles, setExistingFiles] = useState<ExistingFile[]>([]);
    const [filesToRemove, setFilesToRemove] = useState<string[]>([]);
    const findingsFileRef = useRef<HTMLInputElement>(null);
    const solutionsFileRef = useRef<HTMLInputElement>(null);

    // Mic recording
    const [findingsRecording, setFindingsRecording] = useState(false);
    const [solutionsRecording, setSolutionsRecording] = useState(false);
    const [transcribing, setTranscribing] = useState(false);
    const findingsRecorderRef = useRef<MediaRecorder | null>(null);
    const solutionsRecorderRef = useRef<MediaRecorder | null>(null);
    const stopRequestedRef = useRef<{ findings: boolean, solutions: boolean }>({ findings: false, solutions: false });

    useEffect(() => {
        const load = async () => {
            if (!id) return;
            try {
                const [pinInfo, response] = await Promise.all([
                    eventsApi.getPinInfo(id),
                    eventsApi.getPinQuestionsAndAnswers(id),
                ]);

                const name = (pinInfo as any).name ?? (pinInfo as any).Name ?? '';
                setPinName(name);

                if (questionId) {
                    const q = response.questions.find((x: any) => x.id === questionId);
                    if (q) {
                        setQuestionText(q.text);
                        setLinkedCategoryName(q.questionCategory?.name ?? '');
                        const ans = q.answers?.[0];
                        if (ans) {
                            setFindings(ans.findings || '');
                            setProposedSolutions(ans.proposedSolutions || '');
                            if (ans.fileAttachments?.length) {
                                setExistingFiles(ans.fileAttachments.map((f: any) => ({
                                    id: f.id ?? f.Id,
                                    fileName: f.fileName ?? f.FileName,
                                    attachmentSection: f.attachmentSection ?? f.AttachmentSection ?? null,
                                })));
                            }
                        }
                    }
                } else {
                    const catMap = new Map<string, CategoryOption>();
                    for (const q of response.questions) {
                        const cat = q.questionCategory;
                        if (cat && cat.name !== 'SONUÇ VE DEĞERLENDİRME' && !catMap.has(cat.id)) {
                            catMap.set(cat.id, { id: cat.id, name: cat.name, index: cat.index ?? null });
                        }
                    }
                    setCategories([...catMap.values()].sort((a, b) => (a.index ?? 999) - (b.index ?? 999)));
                }
            } catch (e) {
                console.error('BulguEklePage load error:', e);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [id, questionId]);

    useEffect(() => {
        if (!selectedCategoryId || !id) {
            setCategoryQuestions([]);
            setSelectedQuestionId('');
            return;
        }
        const loadQuestions = async () => {
            try {
                const response = await eventsApi.getPinQuestionsAndAnswers(id);
                const qs = response.questions
                    .filter((q: any) => q.questionCategory.id === selectedCategoryId)
                    .map((q: any) => ({ id: q.id, text: q.text }));
                setCategoryQuestions(qs);
                if (qs.length === 1) setSelectedQuestionId(qs[0].id);
            } catch (e) {
                console.error('Load category questions error:', e);
            }
        };
        loadQuestions();
    }, [selectedCategoryId, id]);

    const resolvedQuestionId = questionId || selectedQuestionId;
    const selectedQuestionText = categoryQuestions.find((q) => q.id === selectedQuestionId)?.text ?? '';
    const llmQuestionText = (questionText || selectedQuestionText || '').trim();

    // --- File handling ---
    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, section: 'findings' | 'solutions') => {
        const files = e.target.files;
        if (!files) return;
        const valid = Array.from(files).filter(f => f.size <= 10 * 1024 * 1024);
        if (valid.length) {
            if (section === 'findings') setNewFindingsFiles(prev => [...prev, ...valid]);
            else setNewProposedFiles(prev => [...prev, ...valid]);
        }
        e.target.value = '';
    };

    const removeNewFile = (section: 'findings' | 'solutions', idx: number) => {
        if (section === 'findings') setNewFindingsFiles(prev => prev.filter((_, i) => i !== idx));
        else setNewProposedFiles(prev => prev.filter((_, i) => i !== idx));
    };

    const removeExistingFile = (fileId: string) => {
        setFilesToRemove(prev => [...prev, fileId]);
        setExistingFiles(prev => prev.filter(f => f.id !== fileId));
    };

    // --- Mic handling ---
    const startRecording = async (target: 'findings' | 'solutions') => {
        stopRequestedRef.current[target] = false;
        if (target === 'findings') setFindingsRecording(true);
        else setSolutionsRecording(true);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            if (stopRequestedRef.current[target]) {
                stream.getTracks().forEach(t => t.stop());
                return;
            }
            const recorder = new MediaRecorder(stream);
            const chunks: BlobPart[] = [];

            recorder.ondataavailable = (e) => chunks.push(e.data);
            recorder.onstop = async () => {
                stream.getTracks().forEach(t => t.stop());
                if (chunks.length === 0) return;
                const blob = new Blob(chunks, { type: 'audio/webm' });
                setTranscribing(true);
                try {
                    const res = await eventsApi.getSpeechToText(blob);
                    if (!res.transcription) return;
                    let text: string;
                    try {
                        text = JSON.parse(res.transcription).text;
                    } catch {
                        text = res.transcription;
                    }
                    if (target === 'findings') {
                        setFindings(prev => prev ? `${prev} ${text}` : text);
                    } else {
                        setProposedSolutions(prev => prev ? `${prev} ${text}` : text);
                    }
                } catch (err) {
                    console.error('Transcription error:', err);
                    notifySpeechTranscriptionError(err);
                } finally {
                    setTranscribing(false);
                }
            };

            recorder.start();
            if (target === 'findings') {
                findingsRecorderRef.current = recorder;
            } else {
                solutionsRecorderRef.current = recorder;
            }
        } catch (err) {
            console.error('Microphone access error:', err);
            if (target === 'findings') setFindingsRecording(false);
            else setSolutionsRecording(false);
        }
    };

    const stopRecording = (target: 'findings' | 'solutions') => {
        stopRequestedRef.current[target] = true;
        if (target === 'findings') {
            if (findingsRecorderRef.current && findingsRecorderRef.current.state !== 'inactive') {
                try { findingsRecorderRef.current.stop(); } catch(e) {}
            }
            findingsRecorderRef.current = null;
            setFindingsRecording(false);
        } else if (target === 'solutions') {
            if (solutionsRecorderRef.current && solutionsRecorderRef.current.state !== 'inactive') {
                try { solutionsRecorderRef.current.stop(); } catch(e) {}
            }
            solutionsRecorderRef.current = null;
            setSolutionsRecording(false);
        }
    };

    // --- Save ---
    const handleSave = async () => {
        if (!id || !resolvedQuestionId || !userId) return;
        setSaving(true);
        try {
            const response = await eventsApi.getPinQuestionsAndAnswers(id);
            const q = response.questions.find((x: any) => x.id === resolvedQuestionId);
            const existingId = q?.answers?.[0]?.id;

            if (existingId) {
                const fd = new FormData();
                fd.append('Id', existingId);
                fd.append('QuestionId', resolvedQuestionId);
                fd.append('UserId', userId);
                fd.append('YesNo', 'false');
                fd.append('Findings', findings);
                fd.append('PinId', id);
                fd.append('ProposedSolutions', proposedSolutions);
                fd.append('Description', '');
                const hasSplit = newFindingsFiles.length > 0 || newProposedFiles.length > 0;
                if (hasSplit) {
                    newFindingsFiles.forEach(f => fd.append('FindingsFiles', f));
                    newProposedFiles.forEach(f => fd.append('ProposedSolutionsFiles', f));
                }
                filesToRemove.forEach(fId => fd.append('FileAttachmentIdsToRemove', fId));
                await eventsApi.updateQuestionAnswer(fd);
            } else {
                await eventsApi.saveQuestionAnswers({
                    questionId: resolvedQuestionId,
                    userId,
                    yesNo: false,
                    findings,
                    pinId: id,
                    proposedSolutions,
                    description: '',
                    findingsFiles: newFindingsFiles.length ? newFindingsFiles : undefined,
                    proposedSolutionsFiles: newProposedFiles.length ? newProposedFiles : undefined,
                });
            }
            navigate(`/reports/${id}/checklist`);
        } catch (e) {
            console.error('Save error:', e);
            alert('Kaydetme sırasında hata oluştu.');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => navigate(`/reports/${id}/checklist`);
    const canSave = findings.trim().length > 0 && !!resolvedQuestionId;
    const canGenerateSuggestion = findings.trim().length > 0 && llmQuestionText.length > 0;

    const handleGenerateSuggestion = async () => {
        if (!canGenerateSuggestion || isGeneratingSuggestion) return;
        setIsGeneratingSuggestion(true);
        try {
            const categoryName = linkedCategoryName || categories.find(c => c.id === selectedCategoryId)?.name;
            const response = await eventsApi.generateInsight({
                question: llmQuestionText,
                tespit: findings.trim(),
                promptType: 'finding',
                category: categoryName || undefined,
            });
            setProposedSolutions(response.insight || '');
        } catch (e) {
            console.error('Suggestion generation error:', e);
            alert('Öneri oluşturulurken hata oluştu.');
        } finally {
            setIsGeneratingSuggestion(false);
        }
    };

    if (!id) return null;

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    const findingsExisting = existingFiles.filter(f => f.attachmentSection == null || f.attachmentSection === 1);
    const proposedExisting = existingFiles.filter(f => f.attachmentSection === 2);
    const findingsAll = [
        ...findingsExisting.map(f => ({ type: 'existing' as const, ...f })),
        ...newFindingsFiles.map((f, i) => ({ type: 'new' as const, id: `nf-${i}`, fileName: f.name, idx: i, section: 'findings' as const })),
    ];
    const proposedAll = [
        ...proposedExisting.map(f => ({ type: 'existing' as const, ...f })),
        ...newProposedFiles.map((f, i) => ({ type: 'new' as const, id: `np-${i}`, fileName: f.name, idx: i, section: 'proposed' as const })),
    ];

    const renderToolbar = (target: 'findings' | 'solutions', fileInputRef: React.RefObject<HTMLInputElement>) => {
        const isRec = target === 'findings' ? findingsRecording : solutionsRecording;
        return (
            <Box sx={{ display: 'flex', gap: 1.5, alignSelf: 'flex-end', mb: 1, px: 0.5 }}>
                <input
                    type="file"
                    ref={fileInputRef}
                    multiple
                    accept="image/*,.pdf,.doc,.docx"
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileSelect(e, target)}
                />
                <IconButton
                    size="large"
                    onClick={() => fileInputRef.current?.click()}
                    sx={{ bgcolor: 'rgba(13,61,74,0.08)', borderRadius: '14px', minWidth: 52, minHeight: 52 }}
                >
                    <PhotoCameraIcon sx={{ fontSize: 28, color: '#0D3D4A' }} />
                </IconButton>
                <IconButton
                    size="large"
                    disabled={transcribing}
                    onPointerDown={(e) => {
                        e.preventDefault();
                        e.currentTarget.setPointerCapture(e.pointerId);
                        if (!isRec) startRecording(target);
                    }}
                    onPointerUp={(e) => {
                        e.preventDefault();
                        e.currentTarget.releasePointerCapture(e.pointerId);
                        if (isRec) stopRecording(target);
                    }}
                    onPointerCancel={(e) => {
                        if (isRec) stopRecording(target);
                    }}
                    onContextMenu={(e) => e.preventDefault()}
                    sx={{
                        bgcolor: isRec ? 'rgba(244,67,54,0.12)' : 'rgba(13,61,74,0.08)',
                        borderRadius: '14px',
                        minWidth: 52, minHeight: 52,
                        touchAction: 'none',
                        WebkitUserSelect: 'none',
                    }}
                >
                    {transcribing ? (
                        <CircularProgress size={24} sx={{ color: '#5A7A84' }} />
                    ) : isRec ? (
                        <MicIcon sx={{ fontSize: 28, color: '#f44336' }} />
                    ) : (
                        <MicIcon sx={{ fontSize: 28, color: '#0D3D4A' }} />
                    )}
                </IconButton>
            </Box>
        );
    };

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#EDF4F6' }}>
            {/* Header */}
            <Box sx={{ bgcolor: '#0D3D4A', px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <IconButton onClick={handleCancel} sx={{ color: 'white', p: 1.5, minWidth: 48, minHeight: 48 }}>
                    <ArrowBackIcon sx={{ fontSize: 24 }} />
                </IconButton>
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 10, color: '#7FAAB8', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                        BULGU EKLE
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'white' }}>
                        {pinName}
                    </Typography>
                </Box>
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 2, pb: 12 }}>
                {!isStandalone && (
                    <Typography sx={{ fontSize: 13, color: '#5A7A84', mb: 1.5 }}>
                        {linkedCategoryName && <strong>{linkedCategoryName} — </strong>}
                        {questionText}
                    </Typography>
                )}

                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0D3D4A', mb: 0.5, letterSpacing: '0.05em' }}>
                    BULGU
                </Typography>
                <TextField
                    fullWidth
                    placeholder="Bulgunuzu yazın..."
                    multiline
                    rows={4}
                    value={findings}
                    onChange={(e) => setFindings(e.target.value)}
                    InputProps={{
                        endAdornment: renderToolbar('findings', findingsFileRef),
                    }}
                    sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                />

                {findingsAll.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        {findingsAll.map((f) => (
                            <Chip
                                key={f.id}
                                icon={<InsertDriveFileIcon sx={{ fontSize: 16 }} />}
                                label={f.fileName}
                                size="small"
                                onDelete={() => {
                                    if (f.type === 'existing') removeExistingFile(f.id);
                                    else removeNewFile('findings', (f as { idx: number }).idx);
                                }}
                                deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                                sx={{
                                    maxWidth: 200,
                                    bgcolor: 'white',
                                    border: '1px solid #C8DDE4',
                                    '& .MuiChip-label': { fontSize: 12 },
                                }}
                            />
                        ))}
                    </Box>
                )}

                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0D3D4A', mb: 0.5, letterSpacing: '0.05em' }}>
                    ÖNERİ <Typography component="span" sx={{ fontSize: 11, color: '#5A7A84', fontWeight: 400 }}>(isteğe bağlı · ofiste tamamlanabilir)</Typography>
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                    <Box
                        component="button"
                        onClick={handleGenerateSuggestion}
                        disabled={!canGenerateSuggestion || isGeneratingSuggestion}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 2,
                            py: 1,
                            borderRadius: '10px',
                            border: 'none',
                            bgcolor: '#0D3D4A',
                            color: 'white',
                            fontWeight: 700,
                            fontSize: 13,
                            cursor: (!canGenerateSuggestion || isGeneratingSuggestion) ? 'not-allowed' : 'pointer',
                            opacity: (!canGenerateSuggestion || isGeneratingSuggestion) ? 0.6 : 1,
                            '&:hover': (!canGenerateSuggestion || isGeneratingSuggestion) ? {} : { bgcolor: '#134F60' },
                        }}
                    >
                        {isGeneratingSuggestion ? (
                            <CircularProgress size={15} sx={{ color: 'white' }} />
                        ) : (
                            <AutoAwesomeIcon sx={{ fontSize: 16 }} />
                        )}
                        {isGeneratingSuggestion ? 'Üretiliyor...' : 'Umay ile Oluştur'}
                    </Box>
                </Box>
                <TextField
                    fullWidth
                    placeholder="Önerinizi yazın..."
                    multiline
                    rows={4}
                    value={proposedSolutions}
                    onChange={(e) => setProposedSolutions(e.target.value)}
                    InputProps={{
                        endAdornment: renderToolbar('solutions', solutionsFileRef),
                    }}
                    sx={{ mb: 2.5, '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                />

                {proposedAll.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        {proposedAll.map((f) => (
                            <Chip
                                key={f.id}
                                icon={<InsertDriveFileIcon sx={{ fontSize: 16 }} />}
                                label={f.fileName}
                                size="small"
                                onDelete={() => {
                                    if (f.type === 'existing') removeExistingFile(f.id);
                                    else removeNewFile('solutions', (f as { idx: number }).idx);
                                }}
                                deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                                sx={{
                                    maxWidth: 200,
                                    bgcolor: '#faf5ff',
                                    border: '1px solid #d8b4fe',
                                    '& .MuiChip-label': { fontSize: 12 },
                                }}
                            />
                        ))}
                    </Box>
                )}

                {isStandalone && (
                    <>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#D93B2B', mb: 0.5, letterSpacing: '0.05em' }}>
                            BAŞLIKLA EŞLEŞTİR *
                        </Typography>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <Select
                                value={selectedCategoryId}
                                onChange={(e) => { setSelectedCategoryId(e.target.value); setSelectedQuestionId(''); }}
                                displayEmpty
                                sx={{ borderRadius: '12px', bgcolor: 'white', minHeight: 52 }}
                            >
                                <MenuItem value="" disabled>Başlık seçin (zorunlu)</MenuItem>
                                {categories.map((cat) => (
                                    <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {selectedCategoryId && categoryQuestions.length > 1 && (
                            <>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0D3D4A', mb: 0.5, letterSpacing: '0.05em' }}>
                                    SORU SEÇ
                                </Typography>
                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <Select
                                        value={selectedQuestionId}
                                        onChange={(e) => setSelectedQuestionId(e.target.value)}
                                        displayEmpty
                                        sx={{ borderRadius: '12px', bgcolor: 'white', minHeight: 52 }}
                                    >
                                        <MenuItem value="" disabled>Soru seçin</MenuItem>
                                        {categoryQuestions.map((q) => (
                                            <MenuItem key={q.id} value={q.id}>{q.text}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </>
                        )}
                    </>
                )}
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    position: 'fixed', bottom: 0, left: sidebarWidth, right: 0,
                    bgcolor: '#F2F7F9', borderTop: '1px solid #C8DDE4',
                    p: 2, display: 'flex', gap: 2, zIndex: 40,
                }}
            >
                <Box
                    component="button"
                    onClick={handleCancel}
                    sx={{
                        minWidth: 56, minHeight: 56,
                        border: '2px solid #C8DDE4', borderRadius: '16px',
                        color: '#0D3D4A', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', bgcolor: 'white',
                        '&:hover': { borderColor: '#0D3D4A', bgcolor: 'rgba(13,61,74,0.04)' },
                    }}
                >
                    <ArrowBackIcon sx={{ fontSize: 24 }} />
                </Box>
                <Box
                    component="button"
                    onClick={(!saving && canSave) ? handleSave : undefined}
                    sx={{
                        flex: 1, minHeight: 56, borderRadius: '16px', border: 'none',
                        bgcolor: (!saving && canSave) ? '#0D3D4A' : '#A0BCC4',
                        color: 'white', fontWeight: 700, fontSize: 17,
                        cursor: (!saving && canSave) ? 'pointer' : 'default',
                        fontFamily: 'Inter, sans-serif',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        '&:hover': (!saving && canSave) ? { bgcolor: '#134F60' } : {},
                    }}
                >
                    {saving ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Kaydet →'}
                </Box>
            </Box>
        </Box>
    );
};

export default BulguEklePage;
